import { inject, Injectable } from "@angular/core";
import { environment } from "../../../environments/environment";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { ACCESS_TOKEN } from "../../storage/constants.lib";
import { ReplaySubject } from "rxjs";
import { HccSummaryCalculations } from "../../models/hcc-summary-calculations";
import { DocumentedDxEventGraphItem } from "../../models/documented-dx-event-graph-item";
import { RootCauseTrendItem } from "../../models/root-cause-trend-item";
import { OpptyHccValuationReportItem } from "../../models/oppty-hcc-valuation-report-item";
import { DiagnosisEventMonthlyTrendItem } from "../../models/diagnosis-event-monthly-trend-item";
import { AppStore } from "../../store/app.store";
import { DxSummaryLineItem } from "../../models/dx-summary-line-item";

@Injectable({
    providedIn: 'root'
})

export class CalculationsService {
  private endpoint = `${environment.baseURL}/api/`;
  private appStore = inject(AppStore);

  hccSummaryCalculations: ReplaySubject<HccSummaryCalculations>;
  hccSummaryCalculationsRange: ReplaySubject<HccSummaryCalculations[]>;
  rootCauseTrendCalculations: ReplaySubject<RootCauseTrendItem[]>;
  hccValuation: ReplaySubject<OpptyHccValuationReportItem[]>;

  lastRefreshDate: ReplaySubject<Date>;

  hccValuationClosedRowName = 'Closed';
  hccValuationOpenRowName = 'Open';
  hccValuationPendingRowName = 'Pending';
  hccValuationTotalRowName = 'Total';

  constructor(private http: HttpClient) { 
    this.hccSummaryCalculations= new ReplaySubject<HccSummaryCalculations>(1);
    this.hccSummaryCalculationsRange= new ReplaySubject<HccSummaryCalculations[]>(1);
    this.rootCauseTrendCalculations = new ReplaySubject<RootCauseTrendItem[]>(1);
    this.hccValuation = new ReplaySubject<OpptyHccValuationReportItem[]>(1);
    this.lastRefreshDate = new ReplaySubject<Date>(1);
  }

  private getHttpOptions(params?: HttpParams) {
    const token = localStorage.getItem(ACCESS_TOKEN);
    let headers = new HttpHeaders();
    headers = headers.append("Authorization", "Bearer " + token);
    const httpOptions = {
      headers: headers,
      params: params
    };

    return httpOptions;
  }

  private getSelectedServiceYear(serviceYear?: number) {
    const selectedServiceYear = serviceYear ?? this.appStore.serviceYear();

    if(selectedServiceYear == null) {
      throw new Error('Service year is not available.');
    }

    return selectedServiceYear;
  }

  getHccSummaryCalculations(serviceYear?: number) {
    const selectedServiceYear = this.getSelectedServiceYear(serviceYear);
    const params = new HttpParams().set('serviceYear', selectedServiceYear.toString());
    return this.http.get<HccSummaryCalculations>(this.endpoint + "calculations/hcc-summary", this.getHttpOptions(params)).subscribe(result => {
        this.hccSummaryCalculations.next(result);
        const refreshDate = result.DateUpdated ? new Date(result.DateUpdated) : null;
        this.lastRefreshDate.next(refreshDate);
    });
  }

  getHccSummaryCalculationsByRange(startYear: number, endYear: number) {
    const params = new HttpParams()
    .set('startYear', startYear.toString())
    .set('endYear', endYear.toString());

    return this.http.get<HccSummaryCalculations[]>(this.endpoint + "calculations/hcc-summary-range", this.getHttpOptions(params)).subscribe(result => {
        this.hccSummaryCalculationsRange.next(result);
    });
  }

  getDocumentedDxEventGraphData() {
    return this.http.get<DocumentedDxEventGraphItem[]>(this.endpoint + "calculations/dx-documented-event-graph", this.getHttpOptions());
  }

  getRootCauseTrend(startYear: number, endYear: number) {
    const params = new HttpParams()
    .set('startYear', startYear.toString())
    .set('endYear', endYear.toString());

    return this.http.get<RootCauseTrendItem[]>(this.endpoint + "calculations/root-cause-trend", this.getHttpOptions(params)).subscribe(result => {
        this.rootCauseTrendCalculations.next(result);
    });
  }

  getOpptyHccValuation(serviceYear?: number) {
    const selectedServiceYear = this.getSelectedServiceYear(serviceYear);
    const params = new HttpParams().set('serviceYear', selectedServiceYear.toString());

    return this.http.get<OpptyHccValuationReportItem[]>(this.endpoint + "calculations/status-valuation", this.getHttpOptions(params));
  }

  getOpptyPreferredActionHccValuation(serviceYear?: number) {
    const selectedServiceYear = this.getSelectedServiceYear(serviceYear);
    const params = new HttpParams().set('serviceYear', selectedServiceYear.toString());

    return this.http.get<OpptyHccValuationReportItem[]>(this.endpoint + "calculations/preferred-action-status-valuation", this.getHttpOptions(params)).subscribe(result => {
      this.hccValuation.next(result);
    });
  }

  getHccPatientDistribution(serviceYear?: number) {
    const selectedServiceYear = this.getSelectedServiceYear(serviceYear);
    const params = new HttpParams().set('serviceYear', selectedServiceYear.toString());

    return this.http.get<any>(this.endpoint + "calculations/patient-distribution", this.getHttpOptions(params));
  }

  getDxMonthlyBreakdownTreand() {
    return this.http.get<DiagnosisEventMonthlyTrendItem[]>(this.endpoint + "calculations/dx-monthly-trend", this.getHttpOptions());
  }

  getDxSummary(serviceYear?: number, payerIDs: number[] = []) {
    let params = new HttpParams().set('serviceYear', this.getSelectedServiceYear(serviceYear).toString());

    const payerIDsParam = payerIDs.length > 0 ? payerIDs.join(',') : '';
    params = params.set('payerIDs', payerIDsParam);

    return this.http.get<DxSummaryLineItem[]>(this.endpoint + "calculations/dx-summary", this.getHttpOptions(params)); 
  }


  //#region Helper Methods
  getHccValuationGrandTotalItems(data: OpptyHccValuationReportItem[]) {
    const rowItems = [];
    let closedTotals = new OpptyHccValuationReportItem();
    let openTotals = new OpptyHccValuationReportItem();
    let pendingTotals = new OpptyHccValuationReportItem();
    let totals = new OpptyHccValuationReportItem();

    let closedTotal = 0;
    let closedHccCount = 0;
    let closedValuation = 0;

    let openTotal = 0;
    let openHccCount = 0;
    let openValuation = 0;

    let pendingTotal = 0;
    let pendingHccCount = 0;
    let pendingValuation = 0;

    const openItems = data.filter(d => d.StateID == 1);
    const closedItems = data.filter(d => d.StateID == 2);
    const pendingItems = data.filter(d => d.StateID == 3);
    const uniqueClosedStatuses = this.getOrderedHccValuationStatuses(closedItems);
    const uniqueOpenStatuses = this.getOrderedHccValuationStatuses(openItems);
    const uniquePendingStatuses = this.getOrderedHccValuationStatuses(pendingItems);

    openItems.forEach(k => {
      openTotal += k.TotalCount ?? 0;
      openHccCount += k.HCCCount ?? 0;
      openValuation += k.Valuation ?? 0;
    });

    closedItems.forEach(k => {
      closedTotal += k.TotalCount ?? 0;
      closedHccCount += k.HCCCount ?? 0;

      //1 = Non-Actionable
      if(k.StatusID != 1) {
        closedValuation += k.Valuation ?? 0;
      }
    });

    pendingItems.forEach(k => {
      pendingTotal += k.TotalCount ?? 0;
      pendingHccCount += k.HCCCount ?? 0;
      pendingValuation += k.Valuation ?? 0;
    });

    closedTotals = {PayerID: 0, PayerName: '', StateID: 2, StatusID: 0, Status: '', TotalCount: closedTotal, HCCCount: closedHccCount, Valuation: closedValuation};
    openTotals = {PayerID: 0, PayerName: '', StateID: 1, StatusID: 0, Status: '', TotalCount: openTotal, HCCCount: openHccCount, Valuation: openValuation};
    pendingTotals = {PayerID: 0, PayerName: '', StateID: 3, StatusID: 0, Status: '', TotalCount: pendingTotal, HCCCount: pendingHccCount, Valuation: pendingValuation};
    totals = {PayerID: 0, PayerName: '', StateID: 1, StatusID: 0, Status: '', TotalCount: openTotal + closedTotal + pendingTotal, HCCCount: openHccCount + closedHccCount + pendingHccCount, Valuation: openValuation + closedValuation + pendingValuation};

    rowItems.push(this.addNewHccValuationCategory(closedTotals, '', this.hccValuationClosedRowName));
    this.setHccValuationCombinedTotalsByStatus(uniqueClosedStatuses, closedItems, rowItems, this.hccValuationClosedRowName);

    rowItems.push(this.addNewHccValuationCategory(openTotals, '', this.hccValuationOpenRowName));
    this.setHccValuationCombinedTotalsByStatus(uniqueOpenStatuses, openItems, rowItems, this.hccValuationOpenRowName);

    rowItems.push(this.addNewHccValuationCategory(pendingTotals, '', this.hccValuationPendingRowName));
    this.setHccValuationCombinedTotalsByStatus(uniquePendingStatuses, pendingItems, rowItems, this.hccValuationPendingRowName);

    rowItems.push(this.addNewHccValuationCategory(totals, '', this.hccValuationTotalRowName));

    return rowItems;
  }

  private getOrderedHccValuationStatuses(items: OpptyHccValuationReportItem[]) {
    const statuses = new Map<string, { statusOrder: number, statusId: number }>();

    items.forEach(item => {
      const status = item.Status?.trim();
      if(!status) {
        return;
      }

      const order = {
        statusOrder: item.StatusOrder ?? Number.MAX_SAFE_INTEGER,
        statusId: item.StatusID ?? Number.MAX_SAFE_INTEGER
      };

      const existingOrder = statuses.get(status);
      if(existingOrder == null
        || order.statusOrder < existingOrder.statusOrder
        || (order.statusOrder == existingOrder.statusOrder && order.statusId < existingOrder.statusId)) {
        statuses.set(status, order);
      }
    });

    return Array.from(statuses.entries())
      .sort((a, b) => {
        if(a[1].statusOrder != b[1].statusOrder) {
          return a[1].statusOrder - b[1].statusOrder;
        }

        if(a[1].statusId != b[1].statusId) {
          return a[1].statusId - b[1].statusId;
        }

        return a[0].localeCompare(b[0]);
      })
      .map(([status]) => status);
  }

  setHccValuationCombinedTotalsByStatus(statusList: string[], itemList: OpptyHccValuationReportItem[], gridRowList: any[], parentName: string) {
    statusList.forEach(s => {
      const items = itemList.filter(i => i.Status?.trim() == s);
      const statusTotals = new OpptyHccValuationReportItem();
      let total = 0;
      let hcc = 0;
      let valuation = 0;

      items.forEach(i => {
        total += i.TotalCount;
        hcc += i.HCCCount;
        valuation += i.Valuation;
      });

      statusTotals.TotalCount = total;
      statusTotals.HCCCount = hcc;
      statusTotals.Valuation = valuation;

      gridRowList.push(this.addNewHccValuationCategory(statusTotals, parentName, s));
    });
  }

  getHccValuationRowItems(data: OpptyHccValuationReportItem[]) {
    const rowItems = [];
    const openItems = data.filter(d => d.StateID == 1);
    const closedItems = data.filter(d => d.StateID == 2);
    const pendingItems = data.filter(d => d.StateID == 3);

    let closedTotals = new OpptyHccValuationReportItem();
    let openTotals = new OpptyHccValuationReportItem();
    let pendingTotals = new OpptyHccValuationReportItem();
    let totals = new OpptyHccValuationReportItem();

    let closedTotal = 0;
    let closedHccCount = 0;
    let closedValuation = 0;

    let openTotal = 0;
    let openHccCount = 0;
    let openValuation = 0;

    let pendingTotal = 0;
    let pendingHccCount = 0;
    let pendingValuation = 0;

    openItems.forEach(k => {
      openTotal += k.TotalCount ?? 0;
      openHccCount += k.HCCCount ?? 0;
      openValuation += k.Valuation ?? 0;
    });

    pendingItems.forEach(k => {
      pendingTotal += k.TotalCount ?? 0;
      pendingHccCount += k.HCCCount ?? 0;
      pendingValuation += k.Valuation ?? 0;
    });

    closedItems.forEach(k => {
      closedTotal += k.TotalCount ?? 0;
      closedHccCount += k.HCCCount ?? 0;

      //1 = Non-Actionable
      if(k.StatusID != 1) {
        closedValuation += k.Valuation ?? 0;
      }
    });

    closedTotals = {PayerID: 0, PayerName: '', StateID: 2, StatusID: 0, Status: '', TotalCount: closedTotal, HCCCount: closedHccCount, Valuation: closedValuation};
    openTotals = {PayerID: 0, PayerName: '', StateID: 1, StatusID: 0, Status: '', TotalCount: openTotal, HCCCount: openHccCount, Valuation: openValuation};
    pendingTotals = {PayerID: 0, PayerName: '', StateID: 3, StatusID: 0, Status: '', TotalCount: pendingTotal, HCCCount: pendingHccCount, Valuation: pendingValuation};
    totals = {PayerID: 0, PayerName: '', StateID: 1, StatusID: 0, Status: '', TotalCount: openTotal + closedTotal + pendingTotal, HCCCount: openHccCount + closedHccCount + pendingHccCount, Valuation: openValuation + closedValuation + pendingValuation};

    if(closedItems?.length > 0) {
      rowItems.push(this.addNewHccValuationCategory(closedTotals, '', this.hccValuationClosedRowName));

      data.filter(d => d.StateID == 2).forEach(k => {
        rowItems.push(this.addNewHccValuationCategory(k, this.hccValuationClosedRowName, k.Status));
      });
    }

    if(openItems?.length > 0) {
      rowItems.push(this.addNewHccValuationCategory(openTotals, '', this.hccValuationOpenRowName));

      data.filter(d => d.StateID == 1).forEach(k => {
        rowItems.push(this.addNewHccValuationCategory(k, this.hccValuationOpenRowName, k.Status));
      });
    }

    if (pendingItems?.length > 0) {
      rowItems.push(this.addNewHccValuationCategory(pendingTotals, '', this.hccValuationPendingRowName));

      data.filter(d => d.StateID == 3).forEach(k => {
        rowItems.push(this.addNewHccValuationCategory(k, this.hccValuationPendingRowName, k.Status));
      });
    }

    rowItems.push(this.addNewHccValuationCategory(totals, '', this.hccValuationTotalRowName));

    return rowItems;
  }

  addNewHccValuationCategory(dataItem: any, parentName: string, displayName: string) {

    if(!dataItem) {
      return;
    }

    const rowItem = {};
    rowItem["DisplayName"] = displayName;
    rowItem["Parent"] = parentName;
    rowItem["TotalCount"] = dataItem.TotalCount;
    rowItem["HCCCount"] = dataItem.HCCCount;
    rowItem["Valuation"] = dataItem.Valuation;

    return rowItem;
  }

  getHccValuationTotal(items: OpptyHccValuationReportItem[]) {
    let total = 0;
    items.forEach(i => total += i.Valuation);
    return total;
  }
  //#endregion
}